import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { User } from '@/shared/api'
import { logoutMutation } from '@/shared/api'
import { UserAvatar } from '@/entities/session'
import { Icon, Popover } from '@/shared/ui'
import s from './ProfileMenu.module.css'

type ProfileMenuProps = {
  user: User
  companyName?: string
  personalAccountUrl?: string
}

/**
 * Меню профиля (prototype/Prototype.dc.html:212-229). Свой поповер, не `features/*`: в отличие
 * от свитчера компаний и уведомлений здесь нет ленты и нет побочного эффекта на открытии —
 * один запрос на выход, поэтому отдельная фича не оправдана (см. Task 16, обоснование в
 * отчёте: почему платформенный свитчер и это меню остались в виджете, а не стали фичами).
 *
 * После выхода — свой сброс состояния, не общий `handleUnauthorized` из `App.tsx`: `logout`
 * отвечает `204` (успех), а не `401`, и глобальный перехватчик реагирует только на `401`.
 */
export function ProfileMenu({ user, companyName, personalAccountUrl }: ProfileMenuProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { mutate: signOut, isPending } = useMutation({
    ...logoutMutation(),
    onSuccess: async () => {
      queryClient.clear()
      await navigate({ to: '/login', replace: true })
    },
  })

  return (
    <Popover
      align="end"
      width={272}
      trigger={
        <button type="button" title="Профиль" className={s.trigger}>
          <UserAvatar user={user} size={28} />
          <Icon name="chevron-down" size={11} strokeWidth={2.4} className={s.chevron} />
        </button>
      }
    >
      <div className={s.info}>
        <UserAvatar user={user} size={42} />
        <span className={s.text}>
          <span className={s.name}>{user.name}</span>
          <span className={s.email}>{user.email}</span>
          <span className={s.role}>{companyName ? `${user.role} · ${companyName}` : user.role}</span>
        </span>
      </div>

      {personalAccountUrl && (
        <a href={personalAccountUrl} className={s.cta}>
          Личный кабинет
          <Icon name="arrow-right" size={15} />
        </a>
      )}

      <button type="button" className={s.logout} onClick={() => signOut({})} disabled={isPending}>
        {/* Класс — ради ховера: текст под курсором уходит в `--ink` (контраст), а иконка остаётся
            красной и держит на себе «это опасное действие». См. ProfileMenu.module.css. */}
        <Icon name="logout" size={15} strokeWidth={1.8} className={s.logoutIcon} />
        Выйти
      </button>
    </Popover>
  )
}
